import * as React from "react";
import { Modal, Table, Input, Button, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  getProductListApi,
  type ProductListItem,
} from "src/api/product/productApi";
import { snackbarStore } from "src/store/snackbarStore";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (products: ProductListItem[]) => void;
  selectedProductIds?: number[];
  initialSelectedProducts?: ProductListItem[];
};

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

export default function ProductSelectModal({ open, onClose, onConfirm, selectedProductIds = [], initialSelectedProducts = [] }: Props) {
  const [loading, setLoading] = React.useState(false);
  const [products, setProducts] = React.useState<ProductListItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [searchText, setSearchText] = React.useState("");
  const [selectedRows, setSelectedRows] = React.useState<ProductListItem[]>(initialSelectedProducts);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<number[]>(selectedProductIds);

  const fetchProducts = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await getProductListApi({
        page,
        size: pageSize,
        order: "productId,desc",
        productName: searchText || undefined,
      });

      if (res.statusCode === 1 && res.data) {
        setProducts(res.data.content);
        setTotal(res.data.page.totalElements);
      } else {
        toast(res.message || "상품 목록을 불러올 수 없습니다.", "error");
      }
    } catch (e: any) {
      toast(e?.message || "상품 목록을 불러오는 중 오류가 발생했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchText]);

  React.useEffect(() => {
    if (open) {
      setSelectedRows(initialSelectedProducts);
      setSelectedRowKeys(selectedProductIds);
      fetchProducts();
    } else {
      setSearchText("");
      setPage(0);
    }
  }, [open, fetchProducts, selectedProductIds, initialSelectedProducts]);

  const handleConfirm = () => {
    onConfirm(selectedRows);
    onClose();
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[], selectedRowsData: ProductListItem[]) => {
      setSelectedRowKeys(selectedKeys as number[]);

      // 현재 페이지에 없는 이미 선택된 항목들을 유지
      const previouslySelected = selectedRows.filter(
        (p) => !products.find((prod) => prod.productId === p.productId)
      );
      const newSelectedRows = [...previouslySelected, ...selectedRowsData];

      // 중복 제거 및 선택 해제된 항목 제거
      const uniqueSelectedRows = newSelectedRows.filter(
        (product, index, self) =>
          selectedKeys.includes(product.productId) &&
          index === self.findIndex((p) => p.productId === product.productId)
      );

      setSelectedRows(uniqueSelectedRows);
    },
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "productId",
      width: 80,
    },
    {
      title: "상품명",
      dataIndex: "productName",
      width: 250,
    },
    {
      title: "상품 코드",
      dataIndex: "productCode",
      width: 150,
    },
    {
      title: "브랜드",
      dataIndex: "brandName",
      width: 120,
      render: (brandName: string) => brandName || "-",
    },
    {
      title: "판매상태",
      dataIndex: "saleStatus",
      width: 100,
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          PREPARING: { label: "준비중", color: "default" },
          ON_SALE: { label: "판매중", color: "green" },
          SOLDOUT: { label: "품절", color: "red" },
          SUSPENDED: { label: "판매중지", color: "orange" },
        };
        const info = statusMap[status] || { label: status, color: "default" };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      title: "가격",
      dataIndex: "baseSalePrice",
      width: 120,
      render: (price: number) => price ? `${price.toLocaleString()}원` : "-",
    },
  ];

  return (
    <Modal
      title="상품 선택 (다중 선택 가능)"
      open={open}
      onCancel={onClose}
      width={1100}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          선택 완료 ({selectedRowKeys.length}개)
        </Button>,
      ]}
      styles={{
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
        },
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="상품명으로 검색"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={fetchProducts}
          allowClear
        />
      </div>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={products}
        rowKey="productId"
        loading={loading}
        pagination={{
          current: page + 1,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p - 1);
            setPageSize(ps);
          },
          showSizeChanger: true,
          showTotal: (total) => `총 ${total}개`,
        }}
        size="small"
        scroll={{ x: 1000 }}
      />
    </Modal>
  );
}
